import { type FC } from "react";

import { isValidSSN } from "@/shared/utils/formatters";

import type { FormField } from "@/shared/types/estateEssentials";

import { AddressBlock } from "./AddressBlock";
import { FieldCell, fieldByKey } from "./FieldCell";

interface SpecialtyReportsFormProps {
  fields: FormField[];
  values: Record<string, string>;
  errors: Record<string, string>;
  onChange: (key: string, value: string) => void;
  deceasedFirstName: string;
  estateId?: number;
}

export const SpecialtyReportsForm: FC<SpecialtyReportsFormProps> = ({
  fields,
  values,
  errors,
  onChange,
  deceasedFirstName,
  estateId,
}) => {
  const v = (key: string) => values[key] ?? "";
  const e = (key: string) => {
    if (errors[key]) return true;
    const field = fieldByKey(fields, key);
    if (!field.required) return false;
    const val = v(key);
    if (field.type === "ssn") return !isValidSSN(val);
    return !val.trim();
  };
  const f = (key: string) => fieldByKey(fields, key);

  const relationshipLabel = deceasedFirstName ? `I am ${deceasedFirstName}'s...` : "I am their...";

  return (
    <div className="space-y-6">
      <p className="text-base font-medium text-primary">
        {deceasedFirstName ? `${deceasedFirstName}'s information` : "Deceased's information"}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FieldCell
          field={f("deceased_full_name")}
          value={v("deceased_full_name")}
          error={e("deceased_full_name")}
          onChange={onChange}
        />
        <FieldCell
          field={f("deceased_ss_number")}
          value={v("deceased_ss_number")}
          error={e("deceased_ss_number")}
          onChange={onChange}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FieldCell
          field={f("date_of_birth")}
          value={v("date_of_birth")}
          error={e("date_of_birth")}
          onChange={onChange}
        />
        <FieldCell
          field={f("date_of_death")}
          value={v("date_of_death")}
          error={e("date_of_death")}
          onChange={onChange}
        />
      </div>

      <AddressBlock
        prefix="deceased"
        values={values}
        onFieldChange={onChange}
        hasError={e}
        estateId={estateId}
      />

      <div className="border-t border-primary pt-6">
        <p className="text-base font-medium text-primary mb-4">Your information</p>

        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FieldCell
              field={f("user_first_name")}
              value={v("user_first_name")}
              error={e("user_first_name")}
              onChange={onChange}
            />
            <FieldCell
              field={f("user_last_name")}
              value={v("user_last_name")}
              error={e("user_last_name")}
              onChange={onChange}
            />
          </div>

          <FieldCell
            field={{ ...f("user_relationship"), label: relationshipLabel }}
            value={v("user_relationship")}
            error={e("user_relationship")}
            onChange={onChange}
          />

          <FieldCell
            field={f("user_phone")}
            value={v("user_phone")}
            error={e("user_phone")}
            onChange={onChange}
          />

          <AddressBlock prefix="user" values={values} onFieldChange={onChange} hasError={e} />
        </div>
      </div>
    </div>
  );
};
